import React, { useMemo, useEffect, useContext, useCallback } from 'react'
import usePrevious from 'hooks/usePrevious'
import { useDispatch } from 'react-redux'
import { useIdRegister } from 'hooks/useIdRegister'
import fetchUsersNotInBoard from 'routes/Board/sagas/fetchUsersNotInBoard'

const DEFAULT_CONTEXT: {
  registerUser: (boardId: number, userId: number) => void;
  unregisterUser: (boardId: number, userId: number) => void;
} = {
  registerUser: () => {},
  unregisterUser: () => {},
}

export const UsersNotInBoardProviderContext = React.createContext(DEFAULT_CONTEXT)

export function UsersNotInBoardProvider ({
  children
}) {
  const dispatch = useDispatch()

  const [ids, registerId, unregisterId] = useIdRegister<string>()
  const prevIds = usePrevious(ids)

  useEffect(() => {
    let addedIds: string[] = []

    if (ids.length) {
      if (prevIds?.length) {
        addedIds = ids.filter(boardId => !prevIds.includes(boardId))
      } else {
        addedIds = ids
      }
    }

    if (addedIds.length) {
      const idsByBoardId = new Map<number, number[]>()

      for (const id of addedIds) {
        let [boardId, userId] = id.split('-')

        if (idsByBoardId.has(Number(boardId))) {
          idsByBoardId.get(Number(boardId))!.push(Number(userId))
        } else {
          idsByBoardId.set(Number(boardId), [Number(userId)])
        }
      }

      dispatch([...idsByBoardId].map(([ boardId, userIds ]) =>
        fetchUsersNotInBoard({
          boardId,
          userIds,
        })
      ))
    }
  }, [ids])

  const registerUser = useCallback((boardId: number, userId: number) => registerId(`${boardId}-${userId}`), [registerId])
  const unregisterUser = useCallback((boardId: number, userId: number) => unregisterId(`${boardId}-${userId}`), [unregisterId])

  const value = useMemo(() => ({
    registerUser,
    unregisterUser,
  }), [registerUser, unregisterUser])

  return (
    <UsersNotInBoardProviderContext.Provider value={value}>
      {children}
    </UsersNotInBoardProviderContext.Provider>
  )
}

export function useUsersNotInBoardProvider (boardId: number, userId: number) {
  const { registerUser, unregisterUser } = useContext(UsersNotInBoardProviderContext)

  useEffect(() => {
    registerUser(boardId, userId)

    return () => {
      unregisterUser(boardId, userId)
    }
  }, [boardId, userId])
}
